import { AlertCircle, CheckCircle, FileText, Upload, X } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import contractsV2Service from "../services/contractsV2Service";
import { UploadStructure, UploadSummary } from "../types/contract";

interface ContractUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ContractUploadModal: React.FC<ContractUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [batchSize, setBatchSize] = useState(700);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadSummary | null>(null);
  const [uploadStructure, setUploadStructure] = useState<UploadStructure | null>(null);
  const [loadingStructure, setLoadingStructure] = useState(false);

  const fetchUploadStructure = useCallback(async () => {
    try {
      setLoadingStructure(true);
      const structure = await contractsV2Service.getUploadStructure();
      setUploadStructure(structure);
    } catch (err) {
      console.error("Failed to fetch upload structure:", err);
    } finally {
      setLoadingStructure(false);
    }
  }, []);

  React.useEffect(() => {
    if (isOpen && !uploadStructure) {
      fetchUploadStructure();
    }
    if (!isOpen) {
      // Reset state when modal closes
      setFile(null);
      setError(null);
      setUploadResult(null);
    }
  }, [isOpen, uploadStructure, fetchUploadStructure]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.type === "text/csv" || droppedFile.name.endsWith(".csv") || droppedFile.name.endsWith(".txt")) {
        setFile(droppedFile);
        setError(null);
        setUploadResult(null);
      } else {
        setError("Please upload a CSV or TXT file");
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file to upload");
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const result = await contractsV2Service.uploadContractsFromFile(file, hasHeader, batchSize);
      setUploadResult(result.summary);
      if (result.success) {
        onSuccess();
        if (result.summary.parseErrors === 0) {
          onClose();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <Upload className={`w-5 h-5 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Upload Contracts
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-full transition-colors ${
              isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* File Format Info */}
          {uploadStructure && (
            <div
              className={`p-3 rounded-lg text-sm ${
                isDarkMode ? "bg-gray-700/50 text-gray-300" : "bg-gray-50 text-gray-600"
              }`}
            >
              <div className="font-medium mb-1">Expected Format:</div>
              <div className="font-mono text-xs">
                Delimiter: <span className="text-blue-500">{uploadStructure.delimiter}</span>
              </div>
              <div className="font-mono text-xs mt-1 break-all">
                Headers: {uploadStructure.headers.slice(0, 5).join(", ")}
                {uploadStructure.headers.length > 5 && ` +${uploadStructure.headers.length - 5} more`}
              </div>
            </div>
          )}

          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDarkMode
                ? "border-gray-600 hover:border-blue-500 hover:bg-gray-700/30"
                : "border-gray-300 hover:border-blue-500 hover:bg-gray-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              aria-label="Contract file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              className="hidden"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className={`w-8 h-8 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
                <div>
                  <div className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {file.name}
                  </div>
                  <div className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Upload
                  className={`w-12 h-12 mx-auto mb-3 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                />
                <div className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  <span className="font-medium text-blue-500">Click to upload</span> or drag and
                  drop
                </div>
                <div className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  CSV or TXT file (max 50MB)
                </div>
              </>
            )}
          </div>

          {/* Options */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Has Header Row
              </label>
              <select
                value={String(hasHeader)}
                onChange={(e) => setHasHeader(e.target.value === "true")}
                className={`w-full px-3 py-2 text-sm border rounded-md ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Batch Size
              </label>
              <select
                value={batchSize}
                onChange={(e) => setBatchSize(Number(e.target.value))}
                className={`w-full px-3 py-2 text-sm border rounded-md ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value={20}>20</option>
                <option value={40}>40</option>
                <option value={60}>60</option>
                <option value={80}>80</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={400}>400</option>
                <option value={600}>600</option>
                <option value={700}>700</option>
              </select>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">{error}</div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div
              className={`p-4 rounded-lg ${
                uploadResult.parseErrors === 0
                  ? isDarkMode
                    ? "bg-green-900/30"
                    : "bg-green-50"
                  : isDarkMode
                  ? "bg-yellow-900/30"
                  : "bg-yellow-50"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle
                  className={`w-5 h-5 ${
                    uploadResult.parseErrors === 0
                      ? isDarkMode
                        ? "text-green-400"
                        : "text-green-600"
                      : isDarkMode
                      ? "text-yellow-400"
                      : "text-yellow-600"
                  }`}
                />
                <span
                  className={`font-medium ${
                    uploadResult.parseErrors === 0
                      ? isDarkMode
                        ? "text-green-400"
                        : "text-green-700"
                      : isDarkMode
                      ? "text-yellow-400"
                      : "text-yellow-700"
                  }`}
                >
                  Upload Complete
                </span>
              </div>
              <div
                className={`text-sm space-y-1 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                <div>Total Parsed: {uploadResult.totalParsed}</div>
                <div>Upserted: {uploadResult.upsertedCount}</div>
                <div>Parse Errors: {uploadResult.parseErrors}</div>
                {uploadResult.failedContracts.length > 0 && (
                  <div className="mt-2">
                    <div className="font-medium text-red-500">Failed Contracts:</div>
                    <ul className="list-disc list-inside text-xs">
                      {uploadResult.failedContracts.map((failedContract, failedContractIndex) => (
                        <li key={`${failedContract.conId ?? "unknown"}-${failedContractIndex}`}>
                          {failedContract.conId === null
                            ? "Unknown conId"
                            : `conId ${failedContract.conId}`}
                          : {failedContract.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-end gap-3 p-4 border-t ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            disabled={uploading}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-600 hover:bg-gray-100"
            } disabled:opacity-50`}
          >
            {uploadResult ? "Close" : "Cancel"}
          </button>
          {!uploadResult && (
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractUploadModal;
